import type {
  EnrichmentCardValue,
  EnrichmentTopicValue,
} from "./types";

export const ENRICHMENT_GROUPS = [
  { id: "essentials", label: "Essentials" },
  { id: "capacity", label: "Capacity" },
  { id: "condition", label: "Condition" },
  { id: "operations", label: "Operations" },
  { id: "water_quality", label: "Water quality" },
  { id: "maintenance", label: "Maintenance" },
  { id: "evidence", label: "Evidence" },
] as const;

export type EnrichmentGroupId = (typeof ENRICHMENT_GROUPS)[number]["id"];

const GROUP_HINTS: Record<EnrichmentGroupId, string[]> = {
  essentials: ["identity", "owner", "operator", "service", "location", "design"],
  capacity: ["capacity", "flow", "volume", "yield", "diameter", "length", "area"],
  condition: ["condition", "status", "issue", "risk", "failure"],
  operations: ["operation", "process", "supply", "demand", "energy", "staff"],
  water_quality: ["quality", "chlor", "turbid", "sample", "compliance"],
  maintenance: ["maintenance", "inspection", "repair", "rehabil", "age"],
  evidence: ["evidence", "source", "document", "photo", "record"],
};

export function enrichmentGroupForTopic(topicKey: string): EnrichmentGroupId {
  const normalised = topicKey.toLowerCase();
  for (const group of ENRICHMENT_GROUPS) {
    if (GROUP_HINTS[group.id].some((hint) => normalised.includes(hint))) {
      return group.id;
    }
  }
  return "essentials";
}

export function emptyEnrichmentTopic(): EnrichmentTopicValue {
  return {
    state: "not_started",
    value: "",
    sourceReference: "",
    informationDate: "",
  };
}

export function buildEnrichmentCards(
  topicKeys: string[],
  existing: Record<string, EnrichmentCardValue> = {},
) {
  const result = Object.fromEntries(
    ENRICHMENT_GROUPS.map((group) => [
      group.id,
      {
        topics: { ...(existing[group.id]?.topics ?? {}) },
        complete: existing[group.id]?.complete ?? false,
      },
    ]),
  ) as Record<string, EnrichmentCardValue>;

  for (const topicKey of topicKeys) {
    const group = enrichmentGroupForTopic(topicKey);
    result[group].topics[topicKey] ??= emptyEnrichmentTopic();
  }
  for (const card of Object.values(result)) {
    const topics = Object.values(card.topics);
    card.complete =
      topics.length > 0 &&
      topics.every((topic) => topic.state !== "not_started");
  }
  return result;
}

export function enrichmentLabel(topicKey: string) {
  return topicKey
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
